import { KeyRound, ShieldAlert } from 'lucide-react'
import { useId, useState } from 'react'

import { ActionFieldset, Button } from '@krp/ui-kit'

import { QueryState } from '@krp/sale-ui'
import type { OwnCashiersVm, RmkActions } from '../../model/types'
import { useNow, waitText } from '../useNow'

const isReady = (status: string) => status === 'ready' || status === 'empty'

/**
 * Блок «Кассир» (RP-CU4): кассиры нашей системы, связанные с кассиром этого кабинета. Кассир выбирает себя и вводит свой код — служба кассиров
 * сама выдаёт подтверждение на его кассира кабинета. Поле скрыто, без автозаполнения; введённое стирается сразу после отправки.
 */
export function OwnCashierStep({ own, actions, onDone }: { own: OwnCashiersVm; actions: RmkActions; onDone: () => void }) {
  const [chosenId, setChosenId] = useState<string | null>(null)
  const [value, setValue] = useState('')
  const [checking, setChecking] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [lockedUntil, setLockedUntil] = useState<string | null>(null)
  const group = useId()
  const field = useId()
  const now = useNow(1000, lockedUntil !== null)
  if (!isReady(own.query.status)) {
    return <QueryState requestIdHidden query={own.query} what="Кассиры" emptyText="Кассиров нет" onRetry={actions.reload} />
  }
  if (own.items.length === 0) {
    return (
      <p className="aqsi-card__text" role="note">
        <ShieldAlert size={14} aria-hidden="true" /> С кассирами этого кабинета ещё не связан ни один кассир нашей системы. Администратор связывает их во
        вкладке «Кассиры» раздела «Чеки и кассиры». Пока можно войти прежним способом ниже.
      </p>
    )
  }
  const chosen = own.items.find((item) => item.cashierId === chosenId) ?? (own.items.length === 1 ? own.items[0] : undefined)
  const locked = lockedUntil !== null && Date.parse(lockedUntil) > now
  const off = chosen === undefined || checking || locked
  const submit = async () => {
    if (chosen === undefined || off || value.trim() === '') return
    const code = value
    setValue(''); setChecking(true); setProblem(null)
    const outcome = await actions.signInOwn(chosen.cashierId, code)
    setChecking(false)
    if (outcome.ok) onDone()
    else { setProblem(outcome.message); setLockedUntil(outcome.lockedUntil) }
  }
  return (
    <>
      <ActionFieldset actionId="aqsi.rmk.own-cashier" legend="Кассир" className="aqsi-form rmk-choice">
        {own.items.map((item) => (
          <label key={item.cashierId} className="krp-check rmk-choice__item" data-selected={item.cashierId === chosen?.cashierId || undefined}>
            <input
              type="radio"
              name={group}
              checked={item.cashierId === chosen?.cashierId}
              onChange={() => { setChosenId(item.cashierId); setProblem(null) }}
            />{' '}
            <span className="rmk-choice__name">{item.displayName}</span>
            {item.position !== null && <span className="aqsi-card__text"> — {item.position}</span>}
          </label>
        ))}
      </ActionFieldset>
      {chosen !== undefined && (
        <form
          className="aqsi-form rmk-code"
          aria-label={`Вход кассира ${chosen.displayName}`}
          onSubmit={(event) => { event.preventDefault(); void submit() }}
        >
          <label className="krp-field__label" htmlFor={field}>
            Личный код: {chosen.displayName}
          </label>
          <div className="rmk-code__row">
            <input
              id={field}
              className="rmk-code__input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={64}
              data-lpignore="true"
              data-1p-ignore="true"
              value={value}
              disabled={off}
              onChange={(event) => setValue(event.target.value)}
            />
            <Button
              type="submit"
              variant="primary"
              icon={<KeyRound size={16} aria-hidden="true" />}
              actionId="aqsi.rmk.own-sign-in"
              busy={checking}
              disabled={off || value.trim() === ''}
              disabledReason={locked ? 'Слишком много неверных кодов: подождите.' : 'Введите код кассира.'}
            >
              Войти
            </Button>
          </div>
          {!chosen.hasCode && <p className="aqsi-card__text">Личный код ещё не задан — войдите кодом по умолчанию.</p>}
        </form>
      )}
      {(checking || problem !== null) && (
        <p className="aqsi-card__text" role={checking ? 'status' : 'alert'}>
          {checking ? 'Проверяем код…' : <><ShieldAlert size={14} aria-hidden="true" /> {locked && lockedUntil !== null ? `Слишком много неверных кодов, повторите через ${waitText(lockedUntil, now)}` : problem}</>}
        </p>
      )}
    </>
  )
}
